/**
 * 地区管理 API 封装层
 * 按后端约定使用 base 路径：/api/admin/locations
 *
 * 参考流程：
 * 1. GET /api/admin/locations/countries - 获取所有国家状态
 * 2. POST /api/admin/locations/sync - 同步（初始化）
 * 3. POST /api/admin/locations/configs/{country_code}/enable - 启用国家
 * 4. GET /api/admin/locations/tree?country_code=xx - 查看地区树
 * 5. PUT /api/admin/locations/{location_id} - 编辑地区
 */
import { request } from '@umijs/max';

const BASE = '/api/admin/locations';

/** 获取所有国家状态（含是否已同步、是否启用等） */
export async function listLocationCountries(
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseListAvailableCountryInfo_>(
    `${BASE}/countries`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** 启用国家（前端写死仅允许柬埔寨 KH） */
export async function enableCountry(
  countryCode: string,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseLocationConfigResponse_>(
    `${BASE}/configs/${countryCode}/enable`,
    {
      method: 'POST',
      ...(options || {}),
    },
  );
}

/** 同步地区数据到数据库（初始化） */
export async function syncLocationData(
  body: API.SyncDataRequest,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseSyncDataResponse_>(`${BASE}/sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取地区树形结构 */
export async function getLocationTree(
  countryCode: string,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseLocationTreeResponse_>(
    `${BASE}/tree`,
    {
      method: 'GET',
      params: { country_code: countryCode },
      ...(options || {}),
    },
  );
}

/** 获取地区详情 */
export async function getLocation(
  locationId: string,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseLocationResponse_>(
    `${BASE}/${locationId}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** 更新地区（display_text、is_active；一级或二级行政区可传 is_hot、hot_sort_order） */
export async function updateLocation(
  locationId: string,
  body: API.LocationUpdateRequest,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseLocationResponse_>(
    `${BASE}/${locationId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

/** 批量设置国家地区展示排序（复用 hot_sort_order 字段，不改变热门标记） */
export async function setLocationSort(
  countryCode: string,
  body: { location_codes: string[] },
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseListLocationResponse_>(
    `${BASE}/countries/${countryCode}/sort`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}

/** 获取国家热门城市列表（一级或二级行政区，按 hot_sort_order 排序） */
export async function listHotCities(
  countryCode: string,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseListLocationResponse_>(
    `${BASE}/countries/${countryCode}/hot`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** 批量设置国家热门城市（一级或二级行政区 code 列表，顺序即展示顺序，空列表清空该国所有热门） */
export async function setHotCities(
  countryCode: string,
  body: API.SetHotCitiesRequest,
  options?: { [key: string]: any },
) {
  return request<API.BaseResponseListLocationResponse_>(
    `${BASE}/countries/${countryCode}/hot`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
      ...(options || {}),
    },
  );
}
